import { Component, Suspense, lazy, useEffect, useState, type ReactNode } from 'react';
import { BrowserRouter, NavLink, Navigate, Route, Routes, useParams } from 'react-router-dom';
import { TaskSheet, newDraft } from './components/TaskSheet';
import { configured, db } from './firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { getLang, setLang, t, useLang } from './lib/i18n';
import { getSelectedDay } from './lib/selectedDay';
import { listenForeground } from './lib/push';
import Login from './pages/Login';
import Today from './pages/Today';
import { JOIN_KEY, useApp } from './state';

// Today and Login load with the app; everything else loads when first opened.
const Plan = lazy(() => import('./pages/Plan'));
const Cycle = lazy(() => import('./pages/Cycle'));
const Shop = lazy(() => import('./pages/Shop'));
const Inbox = lazy(() => import('./pages/Inbox'));
const Coach = lazy(() => import('./pages/Coach'));
const Admin = lazy(() => import('./pages/Admin'));
const More = lazy(() => import('./pages/More'));
const Progress = lazy(() => import('./pages/Progress'));
const Habits = lazy(() => import('./pages/Habits'));
const Guide = lazy(() => import('./pages/Guide'));
const Settings = lazy(() => import('./pages/Settings'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const DuoSetup = lazy(() => import('./pages/DuoSetup'));

/** A crash in one screen shows a way back instead of a blank page. */
class ErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(error: unknown) {
    console.error('DuoPlate screen error', error);
  }
  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <main className="splash">
        <img src="/icons/icon-192.png" alt="" width={64} height={64} />
        <h2>{t('Something went wrong on this screen')}</h2>
        <p className="muted">{t('Your data is safe. Reload to continue.')}</p>
        <button className="btn primary" onClick={() => location.assign('/')}>{t('Reload')}</button>
      </main>
    );
  }
}

/** /join/ABC123 remembers the code through sign-up and onboarding. */
function JoinLink() {
  const { code } = useParams();
  try { if (code) sessionStorage.setItem(JOIN_KEY, code.toUpperCase()); } catch { /* storage blocked */ }
  return <Navigate to="/" replace />;
}

function Splash({ text = '' }: { text?: string }) {
  return (
    <main className="splash">
      <img className="pulse" src="/icons/icon-192.png" alt="" width={64} height={64} />
      {text && <p className="muted">{text}</p>}
    </main>
  );
}

function Toast() {
  const [t, setT] = useState<{ title: string; body: string } | null>(null);
  useEffect(() => {
    let off = () => {};
    listenForeground((title, body) => { setT({ title, body }); setTimeout(() => setT(null), 6000); }).then((u) => { off = u; });
    return () => off();
  }, []);
  if (!t) return null;
  return <div className="toast" role="status"><b>{t.title}</b><span>{t.body}</span></div>;
}

const ICONS: Record<string, ReactNode> = {
  today: <path d="M4 7.5h16M8 3.5v4M16 3.5v4M5.5 5.5h13a1.5 1.5 0 0 1 1.5 1.5v11.5a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 18.5V7a1.5 1.5 0 0 1 1.5-1.5ZM8 12h3M8 16h6" />,
  inbox: <path d="M4 13.5 6.2 5.6A1.5 1.5 0 0 1 7.6 4.5h8.8a1.5 1.5 0 0 1 1.4 1.1L20 13.5M4 13.5V18a1.5 1.5 0 0 0 1.5 1.5h13A1.5 1.5 0 0 0 20 18v-4.5M4 13.5h4.5l1 2h5l1-2H20" />,
  plan: <path d="M7 4v7a2 2 0 0 0 2 2v7M7 4v4M10 4v4M13 4v16M13 4c3 0 4 3 4 6h-4M17 10v10" />,
  shop: <path d="M5 7h14l-1.5 9h-11L5 7Zm3 0a4 4 0 0 1 8 0M9 20h.01M15 20h.01" />,
  more: <path d="M5 12h.01M12 12h.01M19 12h.01" />,
  coach: <path d="M12 3l1.8 4.7L18.5 9.5l-4.7 1.8L12 16l-1.8-4.7L5.5 9.5l4.7-1.8L12 3ZM18.5 15l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8.8-2Z" />,
};

function Shell() {
  const { user, profile } = useApp();
  const [adding, setAdding] = useState(false);
  const tabs: [string, string, string][] = [
    ['/', 'today', 'Today'],
    ['/inbox', 'inbox', 'Inbox'],
    ...(profile?.ai ? [['/coach', 'coach', 'Coach'] as [string, string, string]] : []),
    ['/plan', 'plan', 'Plan'],
    ['/shop', 'shop', 'Shop'],
    ['/more', 'more', 'More'],
  ];
  return (
    <>
      <Toast />
      <Suspense fallback={<Splash />}>
        <Routes>
          <Route path="/" element={<Today />} />
          <Route path="/plan" element={<Plan />} />
          <Route path="/cycle" element={<Cycle />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/inbox" element={<Inbox />} />
          <Route path="/coach" element={<Coach />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/more" element={<More />} />
          <Route path="/progress" element={<Progress />} />
          <Route path="/habits" element={<Habits />} />
          <Route path="/guide" element={<Guide />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <nav className="tabbar" aria-label="Main">
        {tabs.map(([to, icon, label]) => (
          <NavLink key={to} to={to} end={to === '/'}>
            <svg viewBox="0 0 24 24" aria-hidden="true">{ICONS[icon]}</svg>
            {t(label)}
          </NavLink>
        ))}
      </nav>
      <button className="fab" aria-label={t('Add a task')} onClick={() => setAdding(true)}>
        <svg viewBox="0 0 24 24" aria-hidden><path d="M12 5v14M5 12h14" /></svg>
      </button>
      {adding && <TaskSheet id={null} initial={newDraft(user!.uid, getSelectedDay())} allowInbox onClose={() => setAdding(false)} />}
    </>
  );
}

function Gate() {
  const { ready, user, profile, profileLoaded, duo } = useApp();
  // The saved language follows the account; an account without one takes the device's choice.
  const savedLang = profile?.lang;
  useEffect(() => {
    if (!user || !profileLoaded || !profile) return;
    if (savedLang) setLang(savedLang);
    else void updateDoc(doc(db, 'users', user.uid), { lang: getLang() }).catch(() => undefined);
  }, [user, profileLoaded, Boolean(profile), savedLang]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!configured) return <Splash text="Firebase is not configured. Copy web/.env.example to web/.env.local and fill it in." />;
  if (!ready) return <Splash />;
  if (!user) return <Login />;
  if (!profileLoaded) return <Splash />;
  if (!profile || !profile.consent?.health) return <Onboarding />;
  if (!profile.duoId) return <DuoSetup />;
  if (!duo) return <Splash text={t('Loading your duo…')} />;
  return <Shell />;
}

export default function App() {
  useLang(); // re-render everything when the language changes
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Suspense fallback={<Splash />}>
          <Routes>
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/join/:code" element={<JoinLink />} />
            <Route path="*" element={<Gate />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
